import * as React from "react";
import Dialog from "@src/components/Dialog";
import { GetEventRecommendationListsQuery } from "@src/@types/api.d";
import { motion } from "framer-motion";
import {
  BedDouble,
  BookHeart,
  Car,
  ChevronLeft,
  ChevronRight,
  CookingPot,
  ExternalLink,
  Image as ImageIcon,
  MapPin,
  Phone,
  Video,
} from "lucide-react";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";

type Props = {
  eventId: string | number;
  recommendationData: GetEventRecommendationListsQuery;
};

type RouteRecommendationList = NonNullable<
  GetEventRecommendationListsQuery["getEventRecommendationLists"][number]
>;

type RouteRecommendationItem = NonNullable<
  RouteRecommendationList["items"][number]
>;

type RecommendationCategory =
  | "VENUE"
  | "CATERING"
  | "FOOD"
  | "ACCOMMODATION"
  | "TRANSPORTATION"
  | "OTHER"
  | string;

type RecommendationMediaType = "IMAGE" | "VIDEO" | string;

type RecommendationItem = {
  id: string;
  name: string;
  description?: string | null;
  contactInfo?: string | null;
  link?: string | null;
  mediaUrl?: string | null;
  mediaType?: RecommendationMediaType | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type RecommendationList = {
  id: string;
  name: string;
  category?: RecommendationCategory | null;
  description?: string | null;
  items?: RecommendationItem[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

const CATEGORY_LABELS: Record<string, string> = {
  VENUE: "Venue",
  CATERING: "Catering",
  FOOD: "Food",
  ACCOMMODATION: "Accommodation",
  TRANSPORTATION: "Transportation",
  OTHER: "Other",
};

const CATEGORY_META: Record<
  string,
  { icon: React.ComponentType<{ className?: string }> }
> = {
  VENUE: { icon: MapPin },
  CATERING: { icon: CookingPot },
  FOOD: { icon: CookingPot },
  ACCOMMODATION: { icon: BedDouble },
  TRANSPORTATION: { icon: Car },
  OTHER: { icon: BookHeart },
};

const toTitleCase = (value?: string | null): string => {
  if (!value) {
    return "Untitled List";
  }

  return value
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getCategoryLabel = (category?: string | null): string => {
  if (!category) {
    return "Other";
  }

  return CATEGORY_LABELS[category] ?? toTitleCase(category.replace(/_/g, " "));
};

const getCategoryMeta = (category?: string | null) => {
  if (!category) {
    return CATEGORY_META.OTHER;
  }

  return CATEGORY_META[category] ?? CATEGORY_META.OTHER;
};

const MediaFallback: React.FC<{
  category?: RecommendationCategory | null;
  minHeightClassName?: string;
}> = ({ category, minHeightClassName = "min-h-[120px]" }) => {
  const categoryMeta = getCategoryMeta(category);
  const CategoryIcon = categoryMeta.icon;

  return (
    <div
      className={`w-full h-full ${minHeightClassName} bg-[#E8EEF6]/60 flex items-center justify-center`}
      aria-label="No media available"
    >
      <div className="w-14 h-14 rounded-full flex items-center justify-center bg-[#123B66] text-white">
        <CategoryIcon className="w-7 h-7" aria-hidden="true" />
      </div>
    </div>
  );
};

const ItemMedia: React.FC<{
  item: RecommendationItem;
  category?: RecommendationCategory | null;
  minHeightClassName?: string;
}> = ({ item, category, minHeightClassName = "min-h-[120px]" }) => {
  if (!item.mediaUrl) {
    return (
      <MediaFallback
        category={category}
        minHeightClassName={minHeightClassName}
      />
    );
  }

  if (item.mediaType === "VIDEO") {
    return (
      <div className="relative w-full">
        <video
          className={`w-full h-full ${minHeightClassName} object-cover rounded-lg border border-white/30`}
          controls
          preload="metadata"
          aria-label={`${item.name} video preview`}
        >
          <source src={item.mediaUrl} />
          Your browser does not support video preview.
        </video>
        <div className="absolute top-2 right-2 rounded-full bg-black/60 text-white p-1.5">
          <Video className="w-4 h-4" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* eslint-disable-next-line @next/next/no-img-element -- remote media URLs need a mutable fallback source here */}
      <img
        src={item.mediaUrl || flowerBackground.src}
        alt={`${item.name} preview`}
        className={`w-full h-full ${minHeightClassName} object-cover rounded-lg border border-white/30`}
        onError={(event) => {
          const target = event.currentTarget;
          if (!target.src.includes("bw-flower.jpeg")) {
            target.src = flowerBackground.src;
          }
        }}
      />
      <div className="absolute top-2 right-2 rounded-full bg-black/55 text-white p-1.5">
        <ImageIcon className="w-4 h-4" aria-hidden="true" />
      </div>
    </div>
  );
};

const Recommendations: React.FC<Props> = ({ eventId, recommendationData }) => {
  const [currentListIndex, setCurrentListIndex] = React.useState(0);
  const [isItemModalOpen, setIsItemModalOpen] = React.useState(false);
  const [selectedItemData, setSelectedItemData] = React.useState<{
    item: RecommendationItem;
    category?: RecommendationCategory | null;
  } | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const recommendationLists = React.useMemo(
    (): RecommendationList[] =>
      (recommendationData?.getEventRecommendationLists ?? [])
        .filter((list): list is RouteRecommendationList => Boolean(list))
        .map((list) => ({
          id: String(list.id),
          name: list.name,
          category: list.category,
          description: list.description,
          items: (list.items ?? [])
            .filter((item): item is RouteRecommendationItem => Boolean(item))
            .map((item) => ({
              id: String(item.id),
              name: item.name,
              description: item.description,
              contactInfo: item.contactInfo,
              link: item.link,
              mediaUrl: item.mediaUrl,
              mediaType: item.mediaType,
              createdAt:
                typeof item.createdAt === "string"
                  ? item.createdAt
                  : String(item.createdAt),
              updatedAt:
                typeof item.updatedAt === "string"
                  ? item.updatedAt
                  : String(item.updatedAt),
            })),
          createdAt:
            typeof list.createdAt === "string"
              ? list.createdAt
              : String(list.createdAt),
          updatedAt:
            typeof list.updatedAt === "string"
              ? list.updatedAt
              : String(list.updatedAt),
        })),
    [recommendationData]
  );

  React.useEffect(() => {
    if (currentListIndex > recommendationLists.length - 1) {
      setCurrentListIndex(0);
    }
  }, [currentListIndex, recommendationLists.length]);

  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [currentListIndex]);

  const openItemModal = (
    item: RecommendationItem,
    category?: RecommendationCategory | null
  ) => {
    setSelectedItemData({ item, category });
    setIsItemModalOpen(true);
  };

  if (!eventId || recommendationLists.length === 0) {
    return null;
  }

  const activeList = recommendationLists[currentListIndex];
  const ActiveCategoryIcon = activeList
    ? getCategoryMeta(activeList.category).icon
    : null;

  const goToPreviousList = () => {
    if (!recommendationLists.length) return;
    setCurrentListIndex((prev) =>
      prev === 0 ? recommendationLists.length - 1 : prev - 1
    );
  };

  const goToNextList = () => {
    if (!recommendationLists.length) return;
    setCurrentListIndex((prev) =>
      prev === recommendationLists.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section className="w-full py-6 md:py-12 bg-[#E8EEF6]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-20">
        <div className="mb-8 md:mb-10 text-center">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-bauerBodoni text-2xl md:text-4xl font-light text-[#071A2F] uppercase tracking-wider"
          >
            Recommendations
          </motion.h2>
        </div>

        <div className="space-y-8">
          {/* Card */}
          <div className="relative">
            {/* Side nav buttons */}
            <>
              <button
                type="button"
                onClick={goToPreviousList}
                disabled={recommendationLists.length <= 1}
                className="hidden lg:flex absolute -left-[100px] top-1/2 -translate-y-1/2 z-10 w-[50px] h-[50px] rounded-full bg-[#031d40] items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Previous recommendation list"
              >
                <svg
                  className="w-4 h-4 rotate-180"
                  fill="none"
                  stroke="#b18958"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={goToNextList}
                disabled={recommendationLists.length <= 1}
                className="hidden lg:flex absolute -right-[100px] top-1/2 -translate-y-1/2 z-10 w-[50px] h-[50px] rounded-full bg-[#031d40] items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Next recommendation list"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="#b18958"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>

            <article className="border border-[#fff] p-2">
              <div className="bg-white border border-[#123B66]/20 shadow-xl flex flex-col lg:flex-row overflow-hidden">
                {/* Left: category info */}
                <div className="lg:w-[35%] p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-[#123B66]/10 flex flex-col justify-center gap-4">
                  <div className="flex flex-row items-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#123B66] text-white flex-shrink-0">
                      {ActiveCategoryIcon && (
                        <ActiveCategoryIcon className="w-6 h-6" />
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <h3 className="text-2xl md:text-3xl text-[#071A2F] font-bauerBodoni leading-tight uppercase tracking-wide">
                        {toTitleCase(activeList.name)}
                      </h3>
                      <span className="text-xs text-[#123B66] font-openSans uppercase tracking-widest">
                        {getCategoryLabel(activeList.category)}
                      </span>
                    </div>
                  </div>
                  {activeList.description?.trim() && (
                    <p className="text-sm text-[#071A2F] font-openSans opacity-70 leading-relaxed">
                      {activeList.description}
                    </p>
                  )}
                </div>

                {/* Right: items */}
                <div
                  className="lg:w-[65%] p-6 md:p-8 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                  ref={scrollContainerRef}
                >
                  {(activeList.items ?? []).length === 0 ? (
                    <p className="text-sm text-[#071A2F] font-openSans opacity-60">
                      No items in this list yet.
                    </p>
                  ) : (
                    <div className="flex gap-4 pb-2 min-w-min">
                      {(activeList.items ?? []).map((item) => (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            openItemModal(item, activeList.category)
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openItemModal(item, activeList.category);
                            }
                          }}
                          className="w-52 flex-shrink-0 flex flex-col border border-[#b18958] bg-[#F7FAFF] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02]"
                          aria-label={`Open ${item.name} details`}
                        >
                          <div className="w-full h-36 overflow-hidden relative flex-shrink-0">
                            <ItemMedia
                              item={item}
                              category={activeList.category}
                              minHeightClassName="min-h-[144px]"
                            />
                          </div>
                          <div className="flex-1 flex flex-col p-4 gap-2">
                            <h4 className="text-sm font-semibold text-[#071A2F] font-bauerBodoni leading-snug uppercase tracking-wide">
                              {item.name}
                            </h4>
                            {(item.link || item.contactInfo) && (
                              <div className="flex flex-col gap-1 text-xs text-[#123B66] font-openSans">
                                {item.link && (
                                  <span className="inline-flex items-center gap-1.5">
                                    <ExternalLink
                                      className="w-3 h-3 flex-shrink-0"
                                      aria-hidden="true"
                                    />
                                    <span className="truncate">
                                      {item.link}
                                    </span>
                                  </span>
                                )}
                                {item.contactInfo && (
                                  <span className="inline-flex items-center gap-1.5">
                                    <Phone
                                      className="w-3 h-3 flex-shrink-0"
                                      aria-hidden="true"
                                    />
                                    <span className="truncate">
                                      {item.contactInfo}
                                    </span>
                                  </span>
                                )}
                              </div>
                            )}
                            {item.description?.trim() && (
                              <p className="text-xs text-[#071A2F] opacity-60 font-openSans line-clamp-3 leading-relaxed">
                                {item.description}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </article>
          </div>

          {/* Mobile nav buttons */}
          {recommendationLists.length > 1 && (
            <div className="flex items-center justify-center gap-4 lg:hidden">
              <button
                type="button"
                onClick={goToPreviousList}
                className="w-[50px] h-[50px] rounded-full bg-[#031d40] flex items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95"
                aria-label="Previous recommendation list"
              >
                <svg
                  className="w-4 h-4 rotate-180"
                  fill="none"
                  stroke="#b18958"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={goToNextList}
                className="w-[50px] h-[50px] rounded-full bg-[#031d40] flex items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95"
                aria-label="Next recommendation list"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="#b18958"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={isItemModalOpen}
        onOpenChange={setIsItemModalOpen}
        preventCloseAutoFocus
        trigger={<span className="hidden" aria-hidden="true" />}
      >
        {selectedItemData && (
          <div className="w-full max-w-[740px] lg:w-[40vw] lg:max-w-[40vw] mx-auto bg-white p-5 md:p-6 rounded-2xl border-0 md:border md:border-[#123B66]/20 shadow-none md:shadow-xl">
            <div className="flex flex-col gap-4 md:gap-5">
              <div className="w-full h-52 md:h-64 overflow-hidden relative rounded-xl">
                <ItemMedia
                  item={selectedItemData.item}
                  category={selectedItemData.category}
                  minHeightClassName="min-h-full"
                />
              </div>

              <h3 className="text-xl md:text-2xl font-semibold text-[#071A2F] leading-snug font-bauerBodoni uppercase tracking-wide">
                {selectedItemData.item.name}
              </h3>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm md:text-base text-[#123B66] font-openSans">
                {selectedItemData.item.link ? (
                  <a
                    href={selectedItemData.item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#123B66] hover:underline break-all"
                  >
                    <ExternalLink
                      className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0"
                      aria-hidden="true"
                    />
                    {selectedItemData.item.link}
                  </a>
                ) : selectedItemData.item.contactInfo ? (
                  <p className="inline-flex items-center gap-2">
                    <Phone
                      className="w-4 h-4 text-[#123B66]"
                      aria-hidden="true"
                    />
                    {selectedItemData.item.contactInfo}
                  </p>
                ) : (
                  <div
                    className="min-h-[24px] min-w-[140px]"
                    aria-hidden="true"
                  />
                )}

                {selectedItemData.item.link &&
                  (selectedItemData.item.contactInfo ? (
                    <p className="inline-flex items-center gap-2">
                      <Phone
                        className="w-4 h-4 text-[#123B66]"
                        aria-hidden="true"
                      />
                      {selectedItemData.item.contactInfo}
                    </p>
                  ) : (
                    <div
                      className="min-h-[24px] min-w-[140px]"
                      aria-hidden="true"
                    />
                  ))}
              </div>

              <p className="text-sm md:text-base text-[#071A2F] leading-relaxed font-openSans opacity-80">
                {selectedItemData.item.description?.trim() || (
                  <span className="block min-h-[24px]" aria-hidden="true" />
                )}
              </p>
            </div>
          </div>
        )}
      </Dialog>
    </section>
  );
};

export default Recommendations;
